import { z } from "zod";
import { optionalUuid } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

export const ProductSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  // ADR-0052: the manufacturer trade-partner contact_role that makes this
  // product (manufacturer sub-role). Backs the ADR-0051 warranty integrity rule.
  manufacturer_contact_role_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Manufacturer"),
  name: z.string().nullable().describe("Name"),
  description: z.string().nullable().describe("Description"),
  category: z.string().nullable().describe("Category"),
  unit_price: z.number().nullable().describe("Unit Price"),
  unit_type: z.string().nullable().describe("Unit Type"),
  product_code: z.string().nullable().describe("Product Code"),
  specifications: z.record(z.unknown()).nullable().describe("Specifications"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  is_active: z.boolean().describe("Active"),
  image_url: z.string().nullable().describe("Image URL"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Product = z.infer<typeof ProductSchema>;

export const ProductInsertSchema = z.object({
  name: z.string().describe("Name"),
  description: z.string().optional().describe("Description"),
  category: z.string().optional().describe("Category"),
  unit_price: z.number().optional().describe("Unit Price"),
  unit_type: z.string().optional().describe("Unit Type"),
  product_code: z.string().optional().describe("Product Code"),
  specifications: z.any().optional().describe("Specifications"),
  custom_fields: z.any().optional().describe("Custom Fields"),
  // ADV-1035: import pipeline validates every row (including update-bound
  // rows) against this schema; a Zod default would inject true on rows whose
  // source CSV omits the column, silently reactivating deactivated products.
  // core.products.is_active carries DEFAULT true at the DB layer, so genuine
  // creates with the column absent still land is_active=true.
  is_active: z.boolean().optional().describe("Active"),
  image_url: z.string().optional().describe("Image URL"),
  manufacturer_contact_role_id: optionalUuid().describe("Manufacturer"),
});

export const ProductUpdateSchema = ProductInsertSchema.partial();

export const ProductQuerySchema = z.object({
  category: z.string().optional(),
  is_active: z
    .string()
    .transform((v) => v === "true")
    .optional(),
  search: z.string().optional(),
});
